import React from 'react';
import {type CellContext, type RowData} from '@tanstack/react-table';
import {
	ActionMenuIconButton,
	ActionMenuIconButtonProps,
} from '@netx/core-components/ActionMenuIconButton';
import {useArrowFocusTrapItem} from '@netx/core-components/ArrowFocusTrapNext';
import {stopPropagation} from '@netx/core-next/utils/browser';
import {splitEventHandler} from '@netx/core-next/utils/fn';

export interface ActionCellProps<TData extends RowData, TValue = any>
	extends CellContext<TData, TValue>,
		ActionMenuIconButtonProps {}

const ActionCellPrimitive = <TData extends RowData, TValue = any>({
	row,
	actions,
	cell,
	column,
	table,
	getValue,
	renderValue,
	...props
}: ActionCellProps<TData, TValue>) => {
	const arrowFocusTrapItem = useArrowFocusTrapItem({
		index: row.index,
	});

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div
			onMouseDownCapture={(event) => {
				stopPropagation(event);
				arrowFocusTrapItem.setActive();
			}}
		>
			<ActionMenuIconButton
				data-testid="row-action-menu"
				alwaysUseEllipsis
				onAction={(action) => action.cb()}
				actions={actions}
				tabIndex={arrowFocusTrapItem.attributes.tabIndex}
				{...props}
				onClick={splitEventHandler(props.onClick, () => {
					arrowFocusTrapItem.setActive();
				})}
				IconButtonProps={{
					onBlur: arrowFocusTrapItem.actionAttributes.onBlur,
					onFocus: arrowFocusTrapItem.actionAttributes.onFocus,
					...props.IconButtonProps,
				}}
				TransitionProps={{
					onEnter: arrowFocusTrapItem.handleMenuOpen,
					onExit: arrowFocusTrapItem.handleMenuClose,
					...props.TransitionProps,
				}}
			/>
		</div>
	);
};
/**
 * `ActionCell` renders an interactive table cell with an `IconButton` that triggers a menu or action, supporting accessibility and visual transitions.
 */
export const ActionCell = ActionCellPrimitive;
export default ActionCell;
